// "You are here": the trail under the top bar, and the browser tab's title.
//
// Built from the MENU TREE and the catalogue, never from the address text. Addresses do
// not follow the sections: Governance pages live under /reports and /settings, Grow
// under /lms. A page's section is the top-level menu row it sits under, whatever its
// address says.
import type { MenuItem } from '../stores/menuStore';
import { normalisePath, sectionByIndexPath, sectionByMenuCode } from './catalog';
import { DASHBOARDS_TAB_PATH, DASHBOARD_HOME_PATH, dashLabel, dashTabFromSlug } from './dashboards';
import { pageMenuItems, topLevelOf, type MenuRules } from './menuTree';
import { SEARCH_PATH } from './searchDestinations';
import { MESSAGES_PATH } from '../components/messages/messagesPath';

export interface TrailItem {
  label: string;
  /** Where the item leads. Absent on the page itself, and on "Search", which is not a place. */
  to?: string;
}

// Screens that have no menu row of their own, and the page each one belongs to. Checked
// in order, so the fixed word "new" is matched before the id pattern that would take it.
const SEGMENT = '[^/]+';
const DETAIL_SCREENS: Array<{ pattern: RegExp; parent: string; label: string }> = [
  { pattern: new RegExp(`^/reports/task-response/${SEGMENT}$`), parent: '/reports/task-response', label: 'Responses' },
  { pattern: /^\/lms\/content\/new$/, parent: '/lms/content', label: 'New module' },
  { pattern: new RegExp(`^/lms/content/${SEGMENT}$`), parent: '/lms/content', label: 'Edit module' },
  { pattern: new RegExp(`^/lms/content/${SEGMENT}/preview$`), parent: '/lms/content', label: 'Preview' },
  { pattern: new RegExp(`^/lms/content/${SEGMENT}/assignments$`), parent: '/lms/content', label: 'Assignments' },
  { pattern: /^\/lms\/journeys\/new$/, parent: '/lms/journeys', label: 'New journey' },
  { pattern: new RegExp(`^/lms/journeys/${SEGMENT}$`), parent: '/lms/journeys', label: 'Edit journey' },
  { pattern: new RegExp(`^/lms/journeys/${SEGMENT}/assignments$`), parent: '/lms/journeys', label: 'Assignments' },
  { pattern: /^\/lms\/sessions\/new$/, parent: '/lms/sessions', label: 'New session' },
  { pattern: new RegExp(`^/lms/sessions/${SEGMENT}$`), parent: '/lms/sessions', label: 'Edit session' },
  { pattern: new RegExp(`^/lms/sessions/${SEGMENT}/assignments$`), parent: '/lms/sessions', label: 'Assignments' },
  { pattern: new RegExp(`^/lms/sessions/${SEGMENT}/learners$`), parent: '/lms/sessions', label: 'Learners' },
];

const lower = (v: unknown) => String(v ?? '').toLowerCase().trim();

/** A top-level row that has pages under it is a section heading in the top bar, not a page. */
export function isSectionRow(pages: MenuItem[], m: MenuItem): boolean {
  return !m.parent_id && pages.some((p) => p.parent_id === m.id);
}

/**
 * The PAGE an address is. A section row can carry the same address as its first page
 * (My Approvals and Approvals both sit at /approvals), and the top bar always opens the
 * page; so the page wins, and a section row is never returned.
 */
export function pageRowFor(pages: MenuItem[], pathname: string): MenuItem | null {
  const wanted = normalisePath(pathname);
  return pages.find((m) => m.path && normalisePath(m.path) === wanted && !isSectionRow(pages, m)) ?? null;
}

/** The Dashboard's name as the menu has it. */
const dashboardName = (pages: MenuItem[]): string =>
  pages.find((m) => m.path === DASHBOARD_HOME_PATH)?.name || 'Dashboard';

// The section, then the page: only places a person can go to. The subject headings of a
// front page ("Performance and exits") and the menu's own headings are not places and are
// not in the top bar, so the trail does not name them (the owner asked for this, 14 Oct 2026).
function pageTrail(pages: MenuItem[], row: MenuItem): TrailItem[] {
  const top = topLevelOf(pages, row);
  // A top-level page (or one whose parent the person does not hold) is its own section.
  if (top.id === row.id) return [{ label: row.name }];

  const section = sectionByMenuCode(top.code);
  return [section?.indexPath ? { label: top.name, to: section.indexPath } : { label: top.name }, { label: row.name }];
}

/**
 * The trail for an address, for this person's menu. An address the menu and the
 * catalogue cannot place gives an empty trail, and the shell then draws none.
 *
 * `pages` may be the menu as the server sent it or already narrowed: the hiding rules
 * are applied here either way (narrowing twice changes nothing), so a page the top bar
 * hides is never named in the trail.
 *
 * `currentTitle` is the open page's own name, for the two kinds of screen the menu
 * cannot name: a dashboard someone built, and a person or store found by search, each
 * known by an id in its address. Until the page has said what it is called, the trail
 * stops one short: at the Dashboard, or at "Search".
 */
export function trailFor(pathname: string, pages: MenuItem[], rules: MenuRules, currentTitle?: string | null): TrailItem[] {
  const path = normalisePath(pathname);
  const rows = pageMenuItems(pages, rules);

  if (path === DASHBOARD_HOME_PATH) return [{ label: dashboardName(rows) }];
  if (path.startsWith(`${DASHBOARD_HOME_PATH}/`)) {
    const parts = path.slice(DASHBOARD_HOME_PATH.length + 1).split('/');
    // Back to the list of dashboards: the home screen's Dashboards tab, not its default tab.
    const home: TrailItem = { label: dashboardName(rows), to: DASHBOARDS_TAB_PATH };
    if (parts.length === 2 && parts[0] === 'custom' && parts[1]) {
      const title = currentTitle?.trim();
      return [home, ...(title ? [{ label: title }] : [])];
    }
    const tab = parts.length === 1 ? dashTabFromSlug(parts[0]) : null;
    if (!tab) return [];
    return [home, { label: dashLabel(tab) }];
  }

  // "Where do you want to look?" for a person or a store found by search. No menu row
  // names it and the address holds only an id, so the open page says who or what it is.
  if (path === SEARCH_PATH) {
    const title = currentTitle?.trim();
    return [{ label: 'Search' }, ...(title ? [{ label: title }] : [])];
  }

  // The person's own Messages. No menu row names it (the round link in the top bar leads
  // there), and the trail stops at the page: who a conversation is with is said on the
  // page itself, never in the trail or the browser tab's title.
  if (path === MESSAGES_PATH) return [{ label: 'Messages' }];

  const section = sectionByIndexPath(path);
  if (section) {
    const top = rows.find((m) => !m.parent_id && lower(m.code) === section.menuCode);
    return top ? [{ label: top.name }] : [];
  }

  const row = pageRowFor(rows, path);
  if (row) return pageTrail(rows, row);

  const detail = DETAIL_SCREENS.find((d) => d.pattern.test(path));
  if (detail) {
    const parent = pageRowFor(rows, detail.parent);
    if (!parent?.path) return [];
    const above = pageTrail(rows, parent);
    // The section, the page the screen belongs to, then the screen.
    const lead = above.length > 1 ? [above[0]] : [];
    return [...lead, { label: parent.name, to: parent.path }, { label: detail.label }];
  }

  return [];
}

/** The browser tab's title for a trail. */
export function titleForTrail(trail: TrailItem[]): string {
  const last = trail[trail.length - 1]?.label;
  return last ? `${last} – ChannelPlex` : 'ChannelPlex';
}
